/**
 * Notification Preferences
 *
 * Per-category (crew assignments / new events / event changes / announcements
 * / weekly digest) × per-channel (email / text / push) toggles, stored in
 * User.notificationPreferences. Categories can offer a subset of channels
 * (the weekly digest is email-only).
 *
 * The Text column is a MUTE switch on top of the separate verified SMS opt-in
 * (SmsNotificationSettings below it on the page) — it can silence texts, but
 * can never cause a text to someone who hasn't completed the opt-in. Until
 * the opt-in is done, the column renders disabled with a pointer to it.
 *
 * Defaults here mirror backend services/notificationPrefs.ts exactly; the
 * server is the authority, this map only paints unset toggles correctly.
 */

import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { useOrganization } from '../../context/OrganizationContext';
import {
  updateProfile,
  type NotificationPreferences,
  type ChannelPrefs,
} from '../../api/auth';
import { getSmsConsentStatus } from '../../api/smsConsent';
import { getErrorMessage } from '../../api/client';

type Category = 'assignments' | 'newEvents' | 'eventChanges' | 'broadcasts' | 'weeklyDigest' | 'adminAlerts';
type Channel = 'email' | 'sms' | 'push';

// Must match backend DEFAULTS in services/notificationPrefs.ts.
// newEvents is all-off by default: the publish notice reaches the whole org,
// so every channel of it is opt-in.
const DEFAULTS: Record<Category, Required<ChannelPrefs>> = {
  assignments: { email: true, sms: true, push: true },
  newEvents: { email: false, sms: false, push: false },
  eventChanges: { email: true, sms: true, push: true },
  broadcasts: { email: true, sms: true, push: true },
  weeklyDigest: { email: true, sms: false, push: false },
  adminAlerts: { email: true, sms: false, push: false },
};

// `channels` limits which columns render a toggle (the rest show a dash):
// the weekly digest only exists as an email. `adminOnly` rows are hidden for
// CREW in the current org — the server never consults them for crew.
const CATEGORIES: {
  key: Category;
  label: string;
  description: string;
  channels: Channel[];
  adminOnly?: boolean;
}[] = [
  {
    key: 'assignments',
    label: 'Crew assignments',
    description: 'When you are assigned to work an event',
    channels: ['email', 'sms', 'push'],
  },
  {
    key: 'newEvents',
    label: 'New events',
    description: 'When a new event is published that you could work',
    channels: ['email', 'sms', 'push'],
  },
  {
    key: 'eventChanges',
    label: 'Event changes',
    description: 'When an event you are assigned to changes or is cancelled',
    channels: ['email', 'sms', 'push'],
  },
  {
    key: 'broadcasts',
    label: 'Announcements',
    description: 'General messages your organization sends everyone',
    channels: ['email', 'sms', 'push'],
  },
  {
    key: 'weeklyDigest',
    label: 'Weekly digest',
    description: "Your organization's weekly email rundown of upcoming events",
    channels: ['email'],
  },
  {
    key: 'adminAlerts',
    label: 'Admins digest',
    description: 'The daily owners-and-admins email: crew still to be notified, plus staffing for the next 7 days',
    channels: ['email'],
    adminOnly: true,
  },
];

const CHANNELS: { key: Channel; label: string }[] = [
  { key: 'email', label: 'Email' },
  { key: 'sms', label: 'Text' },
  { key: 'push', label: 'Push' },
];

function resolve(prefs: NotificationPreferences | null | undefined): Record<Category, Required<ChannelPrefs>> {
  const out = {} as Record<Category, Required<ChannelPrefs>>;
  for (const { key: category } of CATEGORIES) {
    out[category] = {
      email: prefs?.[category]?.email ?? prefs?.email ?? DEFAULTS[category].email,
      sms: prefs?.[category]?.sms ?? prefs?.sms ?? DEFAULTS[category].sms,
      push: prefs?.[category]?.push ?? DEFAULTS[category].push,
    };
  }
  return out;
}

export default function NotificationPreferenceSettings() {
  const { user, refreshUser } = useAuth();
  const { currentOrg, smsEnabled: orgSmsEnabled, isOrgAdmin } = useOrganization();
  const visibleCategories = CATEGORIES.filter((c) => !c.adminOnly || isOrgAdmin);
  const [values, setValues] = useState(() => resolve(user?.notificationPreferences));
  const [saving, setSaving] = useState(false);
  const [smsEnabled, setSmsEnabled] = useState<boolean | null>(null);

  // Whether the user has completed the verified SMS opt-in — drives the Text
  // column's enabled state and hint. null = still loading (render disabled).
  useEffect(() => {
    let cancelled = false;
    getSmsConsentStatus()
      .then((s) => {
        if (!cancelled) setSmsEnabled(s.enabled);
      })
      .catch(() => {
        if (!cancelled) setSmsEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleToggle = async (category: Category, channel: Channel) => {
    const previous = values;
    const next = {
      ...values,
      [category]: { ...values[category], [channel]: !values[category][channel] },
    };
    setValues(next);
    setSaving(true);

    try {
      // Persist the full resolved grid: explicit values for every toggle
      // (only the channels each category actually offers), so future default
      // changes on the server never flip a choice already made.
      const grid: NotificationPreferences = {};
      for (const cat of visibleCategories) {
        grid[cat.key] = Object.fromEntries(
          cat.channels.map((ch) => [ch, next[cat.key][ch]])
        ) as ChannelPrefs;
      }
      await updateProfile({ notificationPreferences: grid });
      await refreshUser();
      toast.success('Notification preferences saved', { id: 'notification-prefs' });
    } catch (err) {
      setValues(previous);
      toast.error(getErrorMessage(err), { id: 'notification-prefs' });
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Notification Preferences</h2>
      <p className="text-sm text-gray-500 mb-4">
        Choose how you want to hear about what's happening in your organization.
      </p>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="py-2 pr-4 font-medium">Notify me about</th>
              {CHANNELS.map((c) => (
                <th key={c.key} className="py-2 px-3 font-medium text-center w-16">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {visibleCategories.map((cat) => (
              <tr key={cat.key}>
                <td className="py-3 pr-4">
                  <p className="font-medium text-gray-900">{cat.label}</p>
                  <p className="text-xs text-gray-500">{cat.description}</p>
                </td>
                {CHANNELS.map((ch) => {
                  if (!cat.channels.includes(ch.key)) {
                    // This category doesn't exist on this channel (e.g. the
                    // weekly digest is email-only) — show a dash, not a toggle.
                    return (
                      <td key={ch.key} className="py-3 px-3 text-center text-gray-300" aria-hidden>
                        &mdash;
                      </td>
                    );
                  }
                  const smsLocked = ch.key === 'sms' && smsEnabled !== true;
                  return (
                    <td key={ch.key} className="py-3 px-3 text-center">
                      <input
                        type="checkbox"
                        aria-label={`${cat.label} via ${ch.label}`}
                        checked={smsLocked ? false : values[cat.key][ch.key]}
                        disabled={saving || smsLocked}
                        onChange={() => handleToggle(cat.key, ch.key)}
                        className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-rack disabled:opacity-40"
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 space-y-1">
        {smsEnabled !== true && (
          <p className="text-xs text-gray-500">
            Text messages require the verified opt-in in{' '}
            <a href="#sms-notifications" className="text-primary-600 hover:underline">
              Text Message Notifications
            </a>{' '}
            below.
          </p>
        )}
        {!orgSmsEnabled && (
          <p className="text-xs text-gray-500">
            {currentOrg?.orgName || 'Your organization'} doesn't currently send text
            notifications, so Text settings have no effect here.
          </p>
        )}
        <p className="text-xs text-gray-500">
          Push notifications reach devices where you've installed the app and enabled
          notifications.
        </p>
        <p className="text-xs text-gray-500">
          Messages your organization sends about a specific event you're crewed on are always
          delivered by email or text, because they're part of working events on ProgramRow.
        </p>
      </div>
    </div>
  );
}
